import httpService from 'services/httpService';

// localStorage key holding a descriptor of an in-progress upload so it can be resumed after
// a page refresh (the picked File itself can't survive a reload, so the user re-selects it).
export const PENDING_IMPORT_KEY = 'pendingCourseImport';

export const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks
const MAX_RETRIES = 5;
const ENDPOINT = '/courseware/api/offering/import_chunk/';
const STATUS_ENDPOINT = '/courseware/api/offering/import_chunk_status/';

// A cheap, instant identity for the file used to resume an interrupted upload. We deliberately
// avoid hashing the whole file: reading a multi-GB file to hash it throws NotReadableError and
// shows a pointless "preparing" progress bar before any chunk is sent. name+size+lastModified
// uniquely identifies the same picked file for resume purposes; archive integrity is enforced
// on the backend when the reassembled .tar.gz is extracted.
export const getFileIdentity = (file) => `${file.name}-${file.size}-${file.lastModified}`;
// Backwards-compatible internal alias.
const fileIdentity = getFileIdentity;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Cooperative abort: the upload loop and retry wrapper check this flag between chunks.
// Chunks are only 512KB, so stopping before the next chunk aborts the upload near-instantly
// (axios 0.21 has no AbortController support). The backend cancel endpoint then deletes the
// partial CourseFolder + chunks.
let aborted = false;
export const abortCourseImport = () => { aborted = true; };
class UploadAbortedError extends Error {
  constructor() {
    super('UPLOAD_ABORTED');
    this.name = 'UploadAbortedError';
  }
}
export const isUploadAbortedError = (err) => err && err.name === 'UploadAbortedError';

// Retry a chunk POST with exponential backoff so a transient network drop does not
// fail the whole upload. Already-stored chunks are idempotent on the backend
// (unique_together), so retries are safe.
const postWithRetry = async (requestFn, attempts = MAX_RETRIES) => {
  let lastError;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (aborted) throw new UploadAbortedError();
    try {
      return await requestFn();
    } catch (error) {
      if (aborted) throw new UploadAbortedError(); // don't retry an aborted upload
      lastError = error;
      if (attempt < attempts - 1) {
        await sleep(1000 * Math.pow(2, attempt)); // 1s, 2s, 4s, 8s, ...
      }
    }
  }
  throw lastError;
};

// Send the first chunk along with the course form fields. Returns the file_id.
const sendFirstChunk = async (values, chunk, hash, totalChunks) => {
  const file = values.import_file;
  const formData = new FormData();
  formData.append('file', chunk);
  formData.append('file_name', file.name);
  formData.append('total_chunks', totalChunks);
  formData.append('hash', hash);
  formData.append('chunk_index', 0);

  // Append all course form fields (courseCode, title, category, institute, department,
  // enrollment_type, course_type, notification_toAll, anonymity_reveal, image, ...).
  Object.keys(values).forEach((key) => {
    if (key === 'import_file') return; // sent as the chunk, not a field
    formData.append(key, values[key]);
  });

  const response = await postWithRetry(() =>
    httpService.post(ENDPOINT, formData, { timeout: 120000 })
  );
  return response.data.file_id;
};

// Send a subsequent chunk.
const sendNextChunk = async (fileId, chunkIndex, chunk) => {
  const formData = new FormData();
  formData.append('file', chunk);
  formData.append('file_id', fileId);
  formData.append('chunk_index', chunkIndex);

  return postWithRetry(() =>
    httpService.post(ENDPOINT, formData, { timeout: 120000 })
  );
};

// Probe the backend for an in-progress upload of this exact file (by hash) so we can
// resume instead of restarting. Failure here is non-fatal — we just start fresh.
const checkResume = async (hash) => {
  try {
    const response = await httpService.get(`${STATUS_ENDPOINT}?hash=${hash}`);
    if (response.data && response.data.exists) {
      return {
        fileId: response.data.file_id,
        received: new Set(response.data.received_chunks || []),
      };
    }
  } catch (e) {
    // ignore — treat as no resumable upload
  }
  return null;
};

// Probe the backend (by file hash) for a resumable upload and report whether it is still
// incomplete. Used on remount after a refresh to decide whether to offer a "resume" banner.
// Returns { exists, complete, fileId, totalChunks, receivedCount }; `exists` is false on any
// error or when the backend has no record of this file.
export const getResumeStatus = async (hash) => {
  try {
    const response = await httpService.get(`${STATUS_ENDPOINT}?hash=${hash}`);
    const data = response.data;
    if (data && data.exists) {
      const received = Array.isArray(data.received_chunks) ? data.received_chunks : [];
      const totalChunks = data.total_chunks || 0;
      return {
        exists: true,
        complete: totalChunks > 0 && received.length >= totalChunks,
        fileId: data.file_id,
        totalChunks,
        receivedCount: received.length,
      };
    }
  } catch (e) {
    // ignore — treat as no resumable upload
  }
  return { exists: false, complete: false, fileId: null, totalChunks: 0, receivedCount: 0 };
};

// Main entry point: upload a course-import .tar.gz in chunks with resume + retry.
export const uploadCourseImportInChunks = async (values, onProgress) => {
  const file = values.import_file;
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  aborted = false; // reset for this run
  if (onProgress) onProgress(0);
  // Instant identity (no file reading) — upload starts immediately, no dead pre-pass.
  const hash = fileIdentity(file);

  // Try to resume a previous interrupted upload of the same file.
  const resume = await checkResume(hash);
  let fileId = resume ? resume.fileId : null;
  const received = resume ? resume.received : new Set();

  const reportProgress = () => {
    if (onProgress) {
      const done = received.size;
      onProgress(Math.min(100, (done / totalChunks) * 100).toFixed(2));
    }
  };

  // First chunk carries the form metadata; only send it if not already stored.
  if (!received.has(0)) {
    if (aborted) throw new UploadAbortedError();
    const firstChunk = file.slice(0, CHUNK_SIZE);
    fileId = await sendFirstChunk(values, firstChunk, hash, totalChunks);
    received.add(0);
    reportProgress();
  }

  // Remaining chunks (skip any already stored on the backend).
  for (let i = 1; i < totalChunks; i++) {
    if (aborted) throw new UploadAbortedError();
    if (received.has(i)) continue;
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);
    await sendNextChunk(fileId, i, chunk);
    received.add(i);
    reportProgress();
  }

  if (onProgress) onProgress('100.00');
  return { success: true, fileId };
};
